import {useInfiniteQuery, UseInfiniteQueryResult, useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query';
import {useDispatch, useSelector} from 'react-redux';
import {IPaginatedResult, SORT} from '../../../../../incase-domain/search';
import {fetchPresentationListPaginated, fetchPresentationSlides} from '../../../../../service/presentation';
import {IPresentation} from '../../../../../incase-domain/presentation';
import useDebounceSearchTerm from '../../../../../hooks/useDebounceSearchTerm';
import {ISlide} from '../../../../../incase-domain/slide';
import {importPresentationSlide} from '../../../../../service/slides';
import {ISlidePreview} from '../../../../../incase-domain/proptypes/slidePreview';
import {getSlidePreviews} from '../../../../../service/slide.preview';
import {
    updateSelectedPresentationSlideIndexListAction,
    updateSelectedPresentationSlidesAction,
} from '../../../../../store/actions/presentation';
import {selectedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {updateSelectedSlideAction} from '../../../../../store/actions/slide';

const presentations = 'presentations';

interface IFetchPresentationProps {
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
    sortBy?: Record<string, SORT>;
    includeSharedOnes?: boolean;
    enabled?: boolean;
}

interface IFetchSlidesProps {
    presentationId: string;
    size: number;
    page: number;
    enabled: boolean;
    findBookmarked?: boolean;
}

interface IImportSlidesProps {
    presentationId: string;
    slides: string[];
}

export const useFetchPresentations = ({
    pageSize,
    searchTerm,
    sortBy,
    includeSharedOnes = true,
    enabled = true,
}: IFetchPresentationProps): UseInfiniteQueryResult<IPaginatedResult<IPresentation>> => {
    const debouncedSearchTerm = useDebounceSearchTerm(searchTerm, 500);

    return useInfiniteQuery({
        queryKey: [presentations, debouncedSearchTerm, sortBy, includeSharedOnes],
        queryFn: ({pageParam = 1}) => fetchPresentationListPaginated(pageParam, pageSize, debouncedSearchTerm, sortBy, includeSharedOnes),
        enabled,
        getNextPageParam: (lastPage) => {
            return lastPage.pageNumber + 1;
        },
    });
};

export const useFetchSlides = ({
    presentationId,
    size,
    page,
    findBookmarked,
    enabled,
}: IFetchSlidesProps): UseQueryResult<IPaginatedResult<ISlide>> => {
    return useQuery({
        queryKey: [presentationId, size, page, findBookmarked, enabled],
        queryFn: () => fetchPresentationSlides(presentationId, size, page, findBookmarked),
        enabled,
    });
};

export const useImportSlides = (): UseMutationResult<
    {
        slideIndexList: string[];
        newSlides: ISlide[];
    },
    unknown,
    IImportSlidesProps
> => {
    const selectedPresentation = useSelector(selectedPresentationSelector);
    const dispatch = useDispatch();

    return useMutation((variables: IImportSlidesProps) => importPresentationSlide(variables.slides, variables.presentationId), {
        onSuccess: (res) => {
            if (res) {
                const updatedSlides = selectedPresentation.slides;
                updatedSlides.splice(selectedPresentation.slideIndexList.length + 1, 0, ...res.newSlides);
                dispatch(updateSelectedPresentationSlidesAction(updatedSlides));
                dispatch(updateSelectedPresentationSlideIndexListAction(res.slideIndexList));
                dispatch(updateSelectedSlideAction(res.newSlides[0], res.slideIndexList.length - 1, false));
            }
        },
    });
};

export const useFetchSlidePreviews = (slideIds: string[] | undefined): UseQueryResult<ISlidePreview[]> => {
    return useQuery({
        queryKey: [slideIds],
        queryFn: () => getSlidePreviews(slideIds || []),
        enabled: !!slideIds?.length,
    });
};
